import * as React from 'react'

import * as turf from '@turf/turf'
import { Units } from '@turf/turf'
import {
  CopyIcon,
  InfoIcon,
  Link2Icon,
  MapPinIcon,
  SquarePlusIcon,
  Trash2Icon,
} from 'lucide-react'
import mapboxgl, { LngLat } from 'mapbox-gl'
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IGeoJson2DGeographicCoordinates,
  LocationType,
  SitemapItemShapeType,
  SitemapLineArrowPosition,
} from '~/client/graph'
import {
  maxLat,
  maxLng,
  minLat,
  minLng,
} from '~/client/src/desktop/components/GeneralMapViewSetUp/GlobeProperties'
import SitemapItemsColorPicker from '~/client/src/desktop/components/SitemapItemsColorPicker/SitemapItemsColorPicker'
import StruxhubMapboxAddressInput from '~/client/src/desktop/components/StruxhubDesktopInputs/StruxhubMapboxAddressInput/StruxhubMapboxAddressInput'
import StruxhubNumberWithControlsInput from '~/client/src/desktop/components/StruxhubDesktopInputs/StruxhubNumberWithControlsInput/StruxhubNumberWithControlsInput'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import GeoreferencingLabel from '~/client/src/shared/components/GeoreferencingLabel/GeoreferenceingLabel'
import { LibraryIconColorHint } from '~/client/src/shared/components/IconPicker'
import { Loader } from '~/client/src/shared/components/Loader'
import { getMapBoxPlaceFromCoordinates } from '~/client/src/shared/components/MapBoxViewer/mapboxGeocoding'
import {
  mapboxFeatureToAddress,
  MAX_ZOOM_LEVEL,
} from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import CircleShapeCoordinates from '~/client/src/shared/components/SitemapHelpers/models/CircleShapeCoordinates'
import GlobeViewItemProperties from '~/client/src/shared/components/SitemapHelpers/models/GlobeViewItemProperties'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import SitemapCircleProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapCircleProperties'
import MapViewItemDrawnPart from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemDrawnPart'
import SitemapItemProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemProperties'
import SitemapPolyLineProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapPolyLineProperties'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSelect'
import {
  FORBIDDEN_SITE_NAME,
  FORBIDDEN_SITE_NAME_MESSAGE,
} from '~/client/src/shared/constants/locationName'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IGeoPosition from '~/client/src/shared/models/IGeoPosition'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import LocationBaseWithDimensions from '~/client/src/shared/models/LocationObjects/LocationBaseWithDimensions'
import { LocationIntegrationType } from '~/client/src/shared/models/LocationObjects/LocationIntegration'
import ProjectLocation from '~/client/src/shared/models/LocationObjects/ProjectLocation'
import VerticalObject from '~/client/src/shared/models/LocationObjects/VerticalObject'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import ProjectSetUpPageStore from '../../../../ProjectSetUpPage.store'
import { getMapBoxPlaces } from '../../../ProjectWorkspaceSetUp/components/MapBoxView'
import ProjectSettingsStore from '../../../ProjectWorkspaceSetUp/ProjectSettings.store'
import MapViewSetUpStore, { SetUpSteps } from '../../MapViewSetUp.store'
import MapViewItemsSetupStore from '../../stores/MapViewItemsSetup.store'
import SitemapsSetupStore from '../../stores/SitemapsSetup.store'
import BuildingProperties from './BuildingProperties'
import DimensionProperties from './DimensionProperties'
import IconSelector from './IconSelector'
import ObjectAccessibleLevelsProperties from './ObjectAccessibleLevelsProperties'
import ParentsSelector from './ParentSelector'
import PropertySelect, { IPropertySelectOption } from './PropertySelect'
import ShapeSelector from './ShapeSelector'

import Colors from '~/client/src/shared/theme.module.scss'
import './PropertiesPanel.scss'

const propertiesPanel = {
  selectItemToEditProperties: 'Select item to edit properties',
  finishEditing: 'Finish editing',
  objectNestedUnder: 'Object nested under',
  edit: 'Edit',
  name: 'Name',
  shape: 'Shape',
  color: 'Color',
  object: 'Object',
  fills: 'Fills',
  opacity: 'Opacity',
  borders: 'Borders',
  line: 'Line',
  width: 'Width',
  radius: 'Radius',
  divisionStartAngle: 'Division start angle',
  divisionEndAngle: 'Division end angle',
  crane: 'Crane',
  limitSwingRadius: 'Limit swing radius',
  projectLocationProperties: 'Project Location Properties',
  textStyle: 'Text style',
  fontSize: 'Font Size',
  fontColor: 'Font color',
  showTextBox: 'Show Text Box',
  showLabel: 'Show Label',
  showIcon: 'Show Icon',
  showShape: 'Show Shape',
  isClosed: 'Is Closed',
  lineStyle: 'Line Style',
  arrows: 'Arrows',
  icon: 'Icon',
  iconColor: 'Icon color',
  text: 'Text',
}

const NAME_INPUT_MAX_WIDTH = 215
const LETTER_WIDTH = 10

const PERCENT_MODIFIER = 100
const MAX_OPACITY_VALUE = 100
const MIN_OPACITY_VALUE = 0
const OPACITY_STEP = 1

const MAX_LINE_WIDTH_VALUE = 10
const MIN_LINE_WIDTH_VALUE = 1
const LINE_WIDTH_STEP = 1
const DESIMAL_LINE_WIDTH_STEP = 1

const MIN_RADIUS = 1
const MAX_RADIUS = 1000

const MAX_FONT_VALUE = 100
const MIN_FONT_VALUE = 4
const FONT_STEP = 1
const maxDegrees = 360
const maxZoomValue = MAX_ZOOM_LEVEL
const minZoomValue = 0
const zoomStep = 0.01
const bearingStep = 0.1

const coordinatesOfThePoint = 'Coordinates of the point'
const location = 'Location'
const latitude = 'Latitude'
const longitude = 'Longitude'
const saveAlignment = 'Save Alignment'
const georeference = 'Georeferencing'
const zoomOfTheCurrentView = 'Zoom of current view'
const orientationOfTheCurrentView = 'Orientation of the current view'
const planProperties = 'Plan properties'
const dimensionUnits = 'Dimension units'
const linkAddressAndCoordinates = 'Link Address & Coordinates'

interface IProps {
  store: MapViewSetUpStore
  projectSetUpPageStore: ProjectSetUpPageStore
  saveAlignment: () => void
  exitRubber: () => void
  step: SetUpSteps
  viewport: IGeoPosition
  /**
   * Render as an embedded section (e.g. inside the resource card's "Map
   * Properties" section): drop the standalone chrome — the object name input,
   * the "Finish editing" control, and the own full-height scroll container — so
   * it flows inside the host's scroll. The host owns name + save.
   */
  embedded?: boolean

  state?: DesktopInitialState
  locationAttributesStore?: LocationAttributesStore
  sitemapItemsStore?: SitemapItemsStore
  graphExecutorStore?: GraphExecutorStore
  projectDateStore?: ProjectDateStore
  userProjectsStore?: UserProjectsStore
  projectsStore?: ProjectsStore
  eventsStore?: DesktopEventsStore
}

const MAX_ANGLE = 180
const MIN_ANGLE = -180

enum DistanceUnitsTypes {
  feet = 'feet',
  meters = 'meters',
}

// TODO: extract all the text to the top
@inject(
  'state',
  'graphExecutorStore',
  'projectDateStore',
  'projectsStore',
  'eventsStore',
)
@observer
export default class PropertiesPanel extends React.Component<IProps> {
  private readonly projectSettingsStore: ProjectSettingsStore

  @observable private store: MapViewSetUpStore = null
  @observable private isLoading: boolean = false
  @observable private selectedCraneDistanceUnitType: Units =
    DistanceUnitsTypes.feet

  // A marker drag rewrites the coordinate on every pointer sample, and this panel is ONE
  // derivation: renderProperties calls fourteen renderX() methods inline, so reading the
  // coordinate re-renders every field in it. Each StruxhubInput then writes an observable from
  // componentDidUpdate, which React 17 appends to the sync queue it is still draining — so a
  // whole SECOND synchronous render pass runs per sample.
  //
  // These two fields are read, not watched, so during a drag they step rather than stream.
  // Leading edge, so the first sample lands at once and the panel never shows the position of
  // the item selected before it; trailing timer, so the final position always lands. Outside a
  // drag the live value is read, which leaves typing into these inputs untouched.
  private static readonly COORDINATE_STEP_MS = 125
  // ref, not deep: this is a snapshot that is always replaced, never mutated through.
  @observable.ref private steppedPosition: IGeoJson2DGeographicCoordinates =
    null
  private lastCoordinateStepAt: number = 0
  private coordinateStepTimer: number = null
  private stopTrackingCoordinates: IReactionDisposer = null

  private readonly lineArrowPositionOptions: IPropertySelectOption[] = [
    { label: 'None', value: SitemapLineArrowPosition.None },
    { label: 'Start, End', value: SitemapLineArrowPosition.StartEnd },
    {
      label: 'Start, End  (Reversed)',
      value: SitemapLineArrowPosition.StartEndReversed,
    },
    {
      label: 'Start, Middle, End',
      value: SitemapLineArrowPosition.StartMiddleEnd,
    },
    {
      label: 'Start, Middle, End (Reversed)',
      value: SitemapLineArrowPosition.StartMiddleEndReversed,
    },
  ]

  private readonly lineArrowGlobePositionOptions: IPropertySelectOption[] = [
    { label: 'None', value: SitemapLineArrowPosition.None },
    {
      label: 'Start, Middle, End',
      value: SitemapLineArrowPosition.StartMiddleEnd,
    },
    {
      label: 'Start, Middle, End (Reversed)',
      value: SitemapLineArrowPosition.StartMiddleEndReversed,
    },
  ]

  public constructor(props: IProps) {
    super(props)
    this.store = props.store
    this.projectSettingsStore = new ProjectSettingsStore(
      props.projectsStore,
      props.eventsStore,
      props.projectSetUpPageStore,
      props.graphExecutorStore,
      props.projectDateStore,
    )
    makeObservable(this)
  }

  public componentDidMount(): void {
    this.stopTrackingCoordinates = reaction(
      () => this.selectedMapViewItem?.sitemapItem?.coordinates,
      position => this.stepCoordinates(position),
    )
  }

  public componentWillUnmount(): void {
    this.stopTrackingCoordinates?.()
    clearTimeout(this.coordinateStepTimer)
  }

  private stepCoordinates(position: IGeoJson2DGeographicCoordinates): void {
    const sinceLastStep = performance.now() - this.lastCoordinateStepAt
    if (sinceLastStep >= PropertiesPanel.COORDINATE_STEP_MS) {
      this.setSteppedPosition(position)
      return
    }
    if (this.coordinateStepTimer) {
      return
    }
    this.coordinateStepTimer = window.setTimeout(() => {
      this.coordinateStepTimer = null
      this.setSteppedPosition(
        this.selectedMapViewItem?.sitemapItem?.coordinates,
      )
    }, PropertiesPanel.COORDINATE_STEP_MS - sinceLastStep)
  }

  @action
  private setSteppedPosition(position: IGeoJson2DGeographicCoordinates): void {
    this.lastCoordinateStepAt = performance.now()
    this.steppedPosition = position
  }

  // Reading the live coordinate is what puts the whole panel on the drag's render path, so
  // while a drag is in progress it is deliberately not read here.
  private get displayedPosition(): IGeoJson2DGeographicCoordinates {
    return this.store.mapBoxEditorStore?.isEditableItemInDragMode
      ? this.steppedPosition
      : this.selectedMapViewItem?.sitemapItem?.coordinates
  }

  private get isGlobeMode(): boolean {
    return this.store.isGlobeMode
  }

  private get itemProperties():
    | GlobeViewItemProperties
    | SitemapItemProperties {
    return this.isGlobeMode
      ? this.selectedMapViewItem.globeViewItemProperties
      : this.selectedMapViewItem.sitemapItemProperties
  }

  public render(): JSX.Element {
    const { whiteboard: sitemap } = this.store.mapViewItemsSetupStore
    if (!sitemap) {
      return null
    }

    const { embedded } = this.props

    return (
      <div
        className={classList({
          'properties-panel relative': true,
          'full-height no-grow': !embedded,
          'globe-panel': this.isGlobeMode,
        })}
        // Embedded inside the resource card's section: neutralize the standalone
        // panel's fixed 300px width, border and absolute (globe) positioning so
        // it fills the section instead of floating as a narrow box.
        style={
          embedded
            ? {
                position: 'static',
                width: '100%',
                minWidth: 0,
                maxWidth: 'none',
                flexBasis: 'auto',
                border: 'none',
              }
            : undefined
        }
      >
        {this.renderLoader()}
        <div
          className={classList({
            'full-height scrollable': !embedded,
            'unclickable-element': this.shouldShowLoading,
          })}
        >
          {!embedded && this.renderControlButton()}
          {this.renderProperties()}
        </div>
      </div>
    )
  }

  private renderLoader(): JSX.Element {
    return (
      this.shouldShowLoading && (
        <div className="full-height full-width absolute modal-overlay unclickable-element">
          <Loader
            className="col x-center y-center full-height full-width"
            size="lg"
          />
        </div>
      )
    )
  }

  private renderStepPlanProperties(): JSX.Element {
    const { viewport } = this.props

    return (
      <div className="col pa10">
        <div className="text large center pb12 light bold bb-light-cool-grey">
          {planProperties}
        </div>
        <div className="col mt20">
          <StruxhubInput
            label={latitude}
            type="number"
            value={viewport.latitude.toString()}
            isRequired={true}
            onChange={this.changeLat}
            min={minLat}
            max={maxLat}
            negativeOrDecimal={true}
            withDelay={true}
          />
          <StruxhubInput
            label={longitude}
            type="number"
            value={viewport.longitude.toString()}
            isRequired={true}
            onChange={this.changeLng}
            min={minLng}
            max={maxLng}
            negativeOrDecimal={true}
            withDelay={true}
          />
          <StruxhubNumberWithControlsInput
            label={zoomOfTheCurrentView}
            type="number"
            isRequired={true}
            max={maxZoomValue}
            min={minZoomValue}
            step={zoomStep}
            value={viewport.zoom.toFixed(3)}
            onValueChange={this.setZoom}
            onChange={this.handleZoomChangeEvent}
            negativeOrDecimal={true}
            withDelay={true}
          />
          <StruxhubNumberWithControlsInput
            label={orientationOfTheCurrentView}
            type="number"
            isRequired={true}
            max={maxDegrees}
            min={-maxDegrees}
            step={bearingStep}
            value={viewport.bearing.toFixed(2)}
            onChange={this.handleOrientationChangeEvent}
            onValueChange={this.setOrientation}
            negativeOrDecimal={true}
            withDelay={true}
          />
          {this.props.step === SetUpSteps.alignPlan && (
            <>
              <div
                className="action-step-button pointer text large center"
                onClick={this.props.saveAlignment}
              >
                {saveAlignment}
              </div>
              <div
                className="action-step-button pointer text large center reversed"
                onClick={this.props.exitRubber}
              >
                {Localization.translator.cancel}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  private changeLat = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { setViewportProp } = this.store.mapBoxViewerStore
    setViewportProp(Number(event.target.value), 'latitude')
  }

  private changeLng = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { setViewportProp } = this.store.mapBoxViewerStore
    setViewportProp(Number(event.target.value), 'longitude')
  }

  private renderPlanProperties(): JSX.Element {
    const { selectedSitemap } = this.sitemapsSetupStore

    return (
      <div className="col">
        <div className="text bold extra-large center pb12 mt10">
          {planProperties}
        </div>
        <div className="col">
          <div className="text medium pb12 mt10 no-grow w-max-content mx10 bold">
            {georeference}
          </div>
          <div className="row bb-light-cool-grey pa10">
            <GeoreferencingLabel
              isReferenced={selectedSitemap?.isReferenced}
              className="no-grow"
            />
            {this.renderEditGeoreference()}
          </div>
        </div>
      </div>
    )
  }

  @action.bound
  private handleOrientationChangeEvent(
    event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    this.setOrientation(+event.target.value)
  }

  @action.bound
  private setOrientation(value: number): void {
    const bearing = Number(value.toFixed(2))

    const newBearing = isNaN(bearing) ? 0 : bearing
    this.store.mapBoxViewerStore.setViewportProp(newBearing, 'bearing')
  }

  @action.bound
  private handleZoomChangeEvent(
    event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    this.setZoom(+event.target.value)
  }

  @action.bound
  private setZoom(zoom: number): void {
    this.store.mapBoxViewerStore.setViewportProp(zoom, 'zoom')
  }

  private renderEditGeoreference(): JSX.Element {
    if (this.isSelectedSitemapReferenced) {
      return (
        <div className="row x-end">
          <div className="row x-around dialog-footer">
            <div
              onClick={this.onEditGeoreferenceClick}
              className="text light-blue large edit-ref-button bold right mr20 pointer"
            >
              {Localization.translator.edit_verb}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="row x-end">
        <div className="tw:flex tw:items-center tw:justify-around dialog-footer">
          <Button
            id="edit-georeference"
            variant="default"
            onClick={this.onEditGeoreferenceClick}
          >
            {Localization.translator.edit_verb}
          </Button>
        </div>
      </div>
    )
  }

  @action.bound
  private onEditGeoreferenceClick(): void {
    this.store.mapBoxEditorStore.setLeftVisibility()
    if (this.store.mapBoxViewerStore?.mapRef?.getMap?.()) {
      this.store.mapBoxViewerStore.setViewportFromAddress()
    }
    this.store.setStep(SetUpSteps.alignPlan)
  }

  private renderControlButton(): JSX.Element {
    if (!this.selectedMapViewItem) {
      return (
        <div className="text large pb12 mt10">
          {this.props.step
            ? this.renderStepPlanProperties()
            : this.renderPlanProperties()}
        </div>
      )
    }

    return (
      <div className="properties-section pa12 mt10">
        <div className="text title uppercase pb12">{propertiesPanel.edit}</div>
        <Button
          className="tw:w-full"
          disabled={!this.selectedMapViewItem.isValid(this.store.isGlobeMode)}
          onClick={this.saveSelectedMapViewItem}
        >
          {propertiesPanel.finishEditing}
        </Button>
      </div>
    )
  }

  private saveSelectedMapViewItem = async (): Promise<void> => {
    if (this.selectedMapViewItem.displayName === FORBIDDEN_SITE_NAME) {
      return
    }
    this.setLoading(true)

    await this.mapViewItemsSetupStore.saveSelectedMapViewItem(
      false,
      this.selectedMapViewItem?.isDisplayed,
    )

    this.setLoading(false)
  }

  private renderHeader(): JSX.Element {
    const dataObject = this.selectedMapViewItem.dataObject
    const canLocationBeDeleted =
      !dataObject?.is(LocationIntegrationType.MaturixStation) &&
      !dataObject?.is(LocationType.ProjectLocation)
    return (
      <div className="tw:flex tw:items-center pb12">
        <div className="text title uppercase tw:flex-1">
          {propertiesPanel.object}
        </div>
        {canLocationBeDeleted && (
          <Button
            variant="destructive"
            size="icon"
            aria-label={Localization.translator.delete}
            onClick={this.deleteItem}
          >
            <Trash2Icon />
          </Button>
        )}
      </div>
    )
  }

  private renderProperties(): JSX.Element {
    const { isGlobeMode } = this.store
    if (!this.selectedMapViewItem) {
      return null
    }

    return (
      <>
        <div className="properties-section px12 pt12">
          {!this.props.embedded && (
            <>
              {this.renderHeader()}
              {this.renderNameInput()}
            </>
          )}
          {this.renderParentsInput()}
        </div>
        {isGlobeMode && this.renderCoordinatesInfo()}
        {this.renderIconSection()}
        {this.renderColorsSection()}
        {this.renderTextStyle()}
        {this.renderCraneSection()}
        {this.renderLineStyle()}
        {this.renderObjectAccessibleLevelsProperties()}
        {this.renderDimensions()}
        {this.renderBuildingsProperties()}
      </>
    )
  }

  private renderDimensions(): JSX.Element {
    const location = this.selectedMapViewItem?.dataObject
    return location instanceof LocationBaseWithDimensions ? (
      <DimensionProperties dataObject={location} />
    ) : null
  }

  private renderCoordinatesInfo(): JSX.Element {
    const { dataObject } = this.selectedMapViewItem

    const position: IGeoJson2DGeographicCoordinates = this.displayedPosition

    if (!position) {
      return this.renderNotPlaced()
    }

    if (dataObject?.is(LocationType.ProjectLocation)) {
      const { projectAddress } = this.props.state
      const { projectAddressSuggestions } = this.projectSettingsStore
      const projectLocation = dataObject as ProjectLocation
      return (
        <div className="col properties-section px12 pt12">
          <div className="text uppercase title pb12">{location}</div>
          <div className="tw:flex tw:items-center ml5 mt10 mb10">
            <div>
              <Checkbox
                checked={projectLocation.isLinked}
                onCheckedChange={() => this.toggleIsLinked()}
              />
            </div>
            <div className="text no-white-space-wrap large bold pl5">
              {linkAddressAndCoordinates}
            </div>
            <Tooltip>
              <TooltipTrigger
                render={<InfoIcon className="ml8 pointer tw:size-4" />}
              />
              <TooltipContent side="bottom" className="tw:block">
                {this.renderLocationTooltipContent()}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="tw:flex tw:items-center relative">
            {projectLocation?.isLinked && (
              <>
                <Link2Icon className="bg-white tw:shrink-0 tw:text-muted-foreground" />
                <div className="link-border-top ba2-light-cool-grey br-none no-flex absolute" />
                <div className="link-border-bottom ba2-light-cool-grey br-none no-flex absolute" />
              </>
            )}
            <div className="col tw:flex-1">
              <StruxhubMapboxAddressInput
                className="overflow-hidden"
                fullAddressClassName={
                  projectLocation?.isLinked ? 'mw234' : 'mw255'
                }
                label={Localization.translator.address}
                isRequired={true}
                fullAddressLabel={
                  this.newProjectAddressString || this.projectAddressString
                }
                items={projectAddressSuggestions}
                onItemSelect={this.mapBoxOnAddressValueChanged}
                onQueryChange={this.mapBoxOnQueryChange}
                value={projectAddress.address}
              />
              <div className="row gap-10">
                <StruxhubInput
                  label={latitude}
                  type="number"
                  isRequired={true}
                  onChange={this.updateProjectItemLatitude}
                  value={position.latitude?.toString()}
                  negativeOrDecimal={true}
                  max={maxLat}
                  min={minLat}
                  withDelay={true}
                  isRequiredTextHidden={true}
                />
                <StruxhubInput
                  label={longitude}
                  type="number"
                  isRequired={true}
                  onChange={this.updateProjectItemLongitude}
                  value={position.longitude?.toString()}
                  negativeOrDecimal={true}
                  max={maxLng}
                  min={minLng}
                  withDelay={true}
                  isRequiredTextHidden={true}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="col properties-section px12 pt12">
        <div className="text uppercase title pb12">{coordinatesOfThePoint}</div>
        <StruxhubInput
          label={latitude}
          type="number"
          isRequired={true}
          onChange={this.updateItemLatitude}
          value={position.latitude?.toString()}
          negativeOrDecimal={true}
          max={maxLat}
          min={minLat}
          withDelay={true}
        />
        <StruxhubInput
          label={longitude}
          type="number"
          isRequired={true}
          onChange={this.updateItemLongitude}
          value={position.longitude?.toString()}
          negativeOrDecimal={true}
          max={maxLng}
          min={minLng}
          withDelay={true}
        />
      </div>
    )
  }

  private renderNotPlaced(): JSX.Element {
    const store = this.mapViewItemsSetupStore
    if (!store.shouldOfferPlacement) {
      return null
    }
    return (
      <div className="tw:flex tw:flex-col tw:items-start tw:gap-2 tw:px-3 tw:pt-3">
        <span className="tw:text-body-md tw:text-muted-foreground">
          {Localization.translator.notPlacedOnMap}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={store.showSelectedMapViewItem}
        >
          <MapPinIcon data-icon="inline-start" />
          {Localization.translator.placeOnMap}
        </Button>
      </div>
    )
  }

  private renderLocationTooltipContent(): JSX.Element {
    return (
      <div className="location-tooltip-content">
        <div className="mb8">
          <b>If Link is Checked:</b>
        </div>
        <ul className="mb12">
          <li>
            {' '}
            • The Address and Long-Lat change together as you move the Pin.
          </li>
        </ul>
        <div className="mb8">
          <b>If Link is Unchecked:</b>
        </div>
        <ul>
          <li> • Moving the Pin only changes the Long-Lat.</li>
          <li>
            {' '}
            • You can type in a different address without affecting the Pin
            location.
          </li>
        </ul>
      </div>
    )
  }

  private updateProjectItemLatitude = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.updateItemLatitude(event)

    const { dataObject } = this.selectedMapViewItem
    if (
      !dataObject?.is(LocationType.ProjectLocation) ||
      !(dataObject as ProjectLocation).isLinked ||
      !this.selectedMapViewItem.sitemapItem?.coordinates
    ) {
      return
    }

    const { coordinates } = this.selectedMapViewItem.sitemapItem
    const item = await getMapBoxPlaceFromCoordinates(
      new LngLat(coordinates.longitude, coordinates.latitude),
      mapboxgl.accessToken,
    )
    runInAction(() => {
      this.store.mapViewItemsSetupStore.newProjectAddress =
        mapboxFeatureToAddress(item)
    })
  }

  private updateItemLatitude = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (!this.selectedMapViewItem?.sitemapItem?.coordinates) {
      return
    }
    runInAction(() => {
      const newLat = Number(event.target.value)
      const { shapeCoordinates, coordinates } =
        this.selectedMapViewItem.sitemapItem
      shapeCoordinates?.translate(newLat - coordinates.latitude, 0)
      coordinates.latitude = newLat
    })
    this.store.mapBoxViewerStore.setViewportFromItem(this.selectedMapViewItem)
  }

  private updateProjectItemLongitude = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.updateItemLongitude(event)

    const { dataObject } = this.selectedMapViewItem
    if (
      !dataObject?.is(LocationType.ProjectLocation) ||
      !(dataObject as ProjectLocation).isLinked ||
      !this.selectedMapViewItem.sitemapItem?.coordinates
    ) {
      return
    }

    const { coordinates } = this.selectedMapViewItem.sitemapItem
    const item = await getMapBoxPlaceFromCoordinates(
      new LngLat(coordinates.longitude, coordinates.latitude),
      mapboxgl.accessToken,
    )
    runInAction(() => {
      this.store.mapViewItemsSetupStore.newProjectAddress =
        mapboxFeatureToAddress(item)
    })
  }

  private updateItemLongitude = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (!this.selectedMapViewItem?.sitemapItem?.coordinates) {
      return
    }
    runInAction(() => {
      const newLon = Number(event.target.value)
      const { shapeCoordinates, coordinates } =
        this.selectedMapViewItem.sitemapItem
      shapeCoordinates?.translate(0, newLon - coordinates.longitude)
      coordinates.longitude = newLon
    })
    this.store.mapBoxViewerStore.setViewportFromItem(this.selectedMapViewItem)
  }

  @action.bound
  private deleteItem(): void {
    this.mapViewItemsSetupStore.showDeleteConfirmationDialog(
      this.selectedMapViewItem,
    )
  }

  private renderNameInput(): JSX.Element {
    const { selectedMapViewItem, isItemAssignedToSitemap } =
      this.store.mapViewItemsSetupStore
    const shouldShowTag = isItemAssignedToSitemap(selectedMapViewItem)
    const color =
      this.itemProperties.labelProperties?.color || Colors.paletteBrandDark

    return (
      <StruxhubInput
        label={propertiesPanel.name}
        isRequired={true}
        isDisabled={!this.isGlobeMode && this.selectedMapViewItem.isRichTextBox}
        value={this.selectedMapViewItem.displayName}
        isValid={this.selectedMapViewItem.displayName !== FORBIDDEN_SITE_NAME}
        validationMessage={FORBIDDEN_SITE_NAME_MESSAGE}
      >
        {(onFocus, onBlur) => (
          <SitemapAttributeTag
            className="no-flex"
            shouldShowAsTag={shouldShowTag}
            dataObject={this.selectedMapViewItem.dataObject}
            sitemapItem={this.selectedMapViewItem.sitemapItem}
          >
            <input
              className="ba-none no-outline bg-transparent text extra-large line-24"
              type="text"
              value={this.selectedMapViewItem.displayName}
              onChange={this.onItemNameChange}
              onFocus={onFocus}
              onBlur={this.addCurrentStateToHistory.bind(this, onBlur)}
              disabled={
                !this.isGlobeMode && this.selectedMapViewItem.isRichTextBox
              }
              style={{
                width: Math.min(
                  (this.selectedMapViewItem.displayName.length + 1) *
                    LETTER_WIDTH,
                  NAME_INPUT_MAX_WIDTH,
                ),
                color,
              }}
            />
          </SitemapAttributeTag>
        )}
      </StruxhubInput>
    )
  }

  @action.bound
  private onItemNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    this.selectedMapViewItem.setName(event.target.value)
  }

  private renderParentsInput(): JSX.Element {
    const { selectedSitemapItemAllowedParents } = this.mapViewItemsSetupStore
    if (!selectedSitemapItemAllowedParents.length) {
      return null
    }

    return (
      <div className="pb12">
        <ParentsSelector
          label={Localization.translator.lbsParent}
          labelTooltip={Localization.translator.lbsParentDescription}
          store={this.mapViewItemsSetupStore}
        />
      </div>
    )
  }

  private renderIconSection(): JSX.Element {
    const colorPicker = this.renderIconColorPicker()

    return (
      colorPicker && (
        <div className="properties-section px12 pt12">{colorPicker}</div>
      )
    )
  }

  private renderColorsSection(): JSX.Element {
    const dataObject = this.selectedMapViewItem.dataObject
    if (dataObject?.is(LocationIntegrationType.MaturixStation)) {
      return null
    }
    const shapeColorPicker = this.renderShapeColorPicker()
    const bordersSection = this.renderBordersSection()
    if (!shapeColorPicker && !bordersSection) {
      return null
    }
    return (
      <div className="properties-section px12 pt12">
        {shapeColorPicker}
        {bordersSection}
      </div>
    )
  }

  private renderIconColorPicker(): JSX.Element {
    const shouldHideColorPicker =
      !this.itemProperties.iconProperties &&
      !this.itemProperties.labelProperties
    if (
      shouldHideColorPicker ||
      (this.itemProperties.iconProperties
        ? this.isPartPropertiesHidden(MapViewItemDrawnPart.Icon)
        : this.isPartPropertiesHidden(MapViewItemDrawnPart.Label))
    ) {
      return null
    }

    const label = this.selectedMapViewItem.isRichTextBox
      ? propertiesPanel.borders
      : this.itemProperties.iconProperties
        ? propertiesPanel.icon
        : propertiesPanel.text
    return (
      <>
        <div className="text uppercase title pb12">{label}</div>
        <div className="pb12 row y-end">
          <div className="no-grow pr12 pb4">
            <div className="text pb4">{propertiesPanel.icon}</div>
            <div>
              <IconSelector
                item={this.selectedMapViewItem}
                store={this.mapViewItemsSetupStore}
              />
            </div>
          </div>
          <div className="no-grow pr12">
            <div className="text pb4 tw:flex tw:items-center tw:gap-1">
              {propertiesPanel.color}
              <LibraryIconColorHint
                iconName={this.selectedMapViewItem.iconName}
              />
            </div>
            <div>
              <SitemapItemsColorPicker
                value={this.selectedMapViewItem.color}
                onChange={this.onColorChange}
              />
            </div>
          </div>
          {!this.selectedMapViewItem.isDataLess && (
            <div className="row pb8">
              <div className="no-grow">
                <Checkbox
                  checked={this.itemProperties.iconProperties.isDisplayed}
                  onCheckedChange={() => this.toggleIconVisibility()}
                />
              </div>
              <div className="text large pl8">{propertiesPanel.showIcon}</div>
            </div>
          )}
        </div>
      </>
    )
  }

  @action.bound
  private toggleIconVisibility(): void {
    const { iconProperties } = this.itemProperties
    iconProperties.toggleIsDisplayed()
    if (
      !iconProperties.isDisplayed &&
      this.mapViewItemsSetupStore.selectedMapViewItemDrawnPart ===
        MapViewItemDrawnPart.Icon
    ) {
      this.mapViewItemsSetupStore.deselectMapViewItemDrawnPart()
    }
    this.addCurrentStateToHistory()
  }

  @action.bound
  private toggleIsClosedShape(): void {
    if (!this.selectedMapViewItem?.sitemapItem?.shapeCoordinates) {
      return
    }
    this.selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed =
      !this.selectedMapViewItem.sitemapItem.shapeCoordinates.isClosed
  }

  @action.bound
  private toggleShapeVisibility(): void {
    const { shapeProperties } = this.itemProperties
    shapeProperties.toggleIsDisplayed()
    if (
      !shapeProperties.isDisplayed &&
      this.mapViewItemsSetupStore.selectedMapViewItemDrawnPart ===
        MapViewItemDrawnPart.Shape
    ) {
      this.mapViewItemsSetupStore.deselectMapViewItemDrawnPart()
    }
    this.addCurrentStateToHistory()
  }

  @action.bound
  private onColorChange(color: string): void {
    this.selectedMapViewItem.setAllColors(color)
    this.addCurrentStateToHistory()
  }

  private renderShapeColorPicker(): JSX.Element {
    const { isDataLess } = this.selectedMapViewItem
    const { shapeProperties } = this.itemProperties

    if (
      !shapeProperties ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Shape)
    ) {
      return this.renderShapeSelectOnly()
    }

    if (shapeProperties.type === SitemapItemShapeType.Polyline) {
      const poly = shapeProperties as SitemapPolyLineProperties
      if (!poly.isClosed) {
        return this.renderShapeSelectOnly()
      }
    }

    const { fillColor, fillOpacity } = shapeProperties
    return (
      <>
        <div className="col pb12">
          <div className="text title uppercase pb12">
            {propertiesPanel.shape}
          </div>
          {!isDataLess && (
            <div className="row">
              <div className="no-grow">
                <Checkbox
                  checked={shapeProperties.isDisplayed}
                  onCheckedChange={() => this.toggleShapeVisibility()}
                />
              </div>
              <div className="text large pl8">{propertiesPanel.showShape}</div>
            </div>
          )}
        </div>
        <div className="pb12">
          {this.renderShapeSelect()}
          <div className="inline-block vertical-align-middle">
            <div className="inline-block vertical-align-middle pr12">
              <div className="text pb4">{propertiesPanel.shape}</div>
              <div>
                <SitemapItemsColorPicker
                  value={fillColor}
                  opacity={fillOpacity}
                  onChange={this.onFillColorChange}
                />
              </div>
            </div>
            <div className="inline-block vertical-align-middle">
              <StruxhubInput
                label={propertiesPanel.opacity}
                customRightIcon="%"
                type="number"
                isRequiredTextHidden={true}
                step={OPACITY_STEP}
                min={MIN_OPACITY_VALUE}
                max={MAX_OPACITY_VALUE}
                value={Math.round(fillOpacity * PERCENT_MODIFIER).toString()}
                onChange={this.onFillOpacityChange}
                withDelay={true}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  private renderShapeSelect(): JSX.Element {
    if (
      this.selectedMapViewItem.isDataLess ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Shape)
    ) {
      return null
    }

    const {
      copy,
      paste,
      copiedShapeProperties: copiedShape,
      getAllowedShapesForType,
    } = this.mapViewItemsSetupStore

    const isPasteAvailable =
      copiedShape &&
      getAllowedShapesForType(
        this.selectedMapViewItem.dataObject.type,
      ).includes(copiedShape?.type)

    return (
      <div className="row">
        <div className="inline-block vertical-align-top pr12">
          <div className="text pb4">{propertiesPanel.shape}</div>
          <ShapeSelector
            item={this.selectedMapViewItem}
            store={this.mapViewItemsSetupStore}
          />
        </div>
        {this.selectedMapViewItem?.sitemapItem?.shapeCoordinates?.type ===
          SitemapItemShapeType.Polyline && this.renderIsClosedToggle()}
        {!this.isGlobeMode && (
          <div className="tw:flex tw:items-center">
            <CopyIcon
              onClick={copy}
              className={classList({
                'pointer tw:size-4 tw:text-muted-foreground': true,
                'inactive-element': !this.itemProperties.shapeProperties,
              })}
            />
            <div
              className={classList({
                'inactive-element': !this.itemProperties.shapeProperties,
                'text large pl8 tw:flex-1': true,
              })}
            >
              {Localization.translator.copy}
            </div>
            <SquarePlusIcon
              className={classList({
                'pointer tw:size-4 tw:text-muted-foreground': true,
                'inactive-element': !isPasteAvailable,
              })}
              onClick={paste}
            />
            <div
              className={classList({
                'inactive-element': !isPasteAvailable,
                'text large pl8 tw:flex-1': true,
              })}
            >
              {Localization.translator.paste}
            </div>
          </div>
        )}
      </div>
    )
  }

  private renderShapeSelectOnly(): JSX.Element {
    const shapeSelect = this.renderShapeSelect()
    return (
      shapeSelect && (
        <div className="col">
          <div className="text title uppercase pb12">
            {propertiesPanel.shape}
          </div>
          <div className="pb12">{shapeSelect}</div>
        </div>
      )
    )
  }

  private renderIsClosedToggle(): JSX.Element {
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    return (
      <div className="row">
        <div className="no-grow">
          <Checkbox
            checked={shapeCoordinates.isClosed}
            onCheckedChange={() => this.toggleIsClosedShape()}
          />
        </div>
        <div className="text large pl8">{propertiesPanel.isClosed}</div>
      </div>
    )
  }

  @action.bound
  private onFillColorChange(color: string): void {
    const { shapeProperties } = this.itemProperties
    shapeProperties.setFillColor(color)
    this.addCurrentStateToHistory()
  }

  @action.bound
  private onFillOpacityChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    const { shapeProperties } = this.itemProperties
    const percentOpacity = Number(event.target.value)
    const opacity =
      Math.max(Math.min(percentOpacity, 100), 0) / PERCENT_MODIFIER
    shapeProperties.setFillOpacity(opacity)
    this.addCurrentStateToHistory()
  }

  private renderBordersSection(): JSX.Element {
    const { shapeProperties } = this.itemProperties

    if (
      !shapeProperties ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Shape)
    ) {
      return null
    }

    const { lineColor, lineWidth } = shapeProperties

    return (
      <>
        <div className="text title uppercase">{propertiesPanel.borders}</div>
        <div className="inline-block vertical-align-middle pb12">
          <div className="inline-block vertical-align-middle pr12">
            <div className="text pb4">{propertiesPanel.line}</div>
            <SitemapItemsColorPicker
              className="no-grow"
              value={lineColor}
              onChange={this.onLineColorChange}
            />
          </div>
          <div className="inline-block vertical-align-middle">
            <StruxhubInput
              label={propertiesPanel.width}
              customRightIcon="px"
              isRequiredTextHidden={true}
              type="number"
              step={LINE_WIDTH_STEP}
              min={MIN_LINE_WIDTH_VALUE}
              max={MAX_LINE_WIDTH_VALUE}
              value={lineWidth.toString()}
              onChange={this.onLineWidthChange}
              withDelay={true}
            />
          </div>
        </div>
      </>
    )
  }

  @action.bound
  private onLineColorChange(color: string): void {
    const { shapeProperties } = this.itemProperties
    shapeProperties.setLineColor(color)

    if (this.selectedMapViewItem.isDataLess) {
      this.selectedMapViewItem.setColor(color)
    }

    this.addCurrentStateToHistory()
  }

  @action.bound
  private onLineWidthChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { shapeProperties } = this.itemProperties
    const width = Number(event.target.value)
    shapeProperties.setLineWidth(width)
    this.addCurrentStateToHistory()
  }

  @action.bound
  private setDistanceUnitType(
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void {
    this.selectedCraneDistanceUnitType = DistanceUnitsTypes[event.target.value]
  }

  private renderCraneSection(): JSX.Element {
    const { shapeProperties } = this.itemProperties
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    if (
      !shapeProperties ||
      shapeProperties.type !== SitemapItemShapeType.Circle ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Shape)
    ) {
      return null
    }
    const circle = this.isGlobeMode
      ? (shapeCoordinates as CircleShapeCoordinates)
      : (shapeProperties as SitemapCircleProperties)

    return (
      <div className="properties-section pa12">
        <div className="text title uppercase pb12">{propertiesPanel.crane}</div>
        {this.isGlobeMode && (
          <StruxhubSelect
            label={dimensionUnits}
            value={this.selectedCraneDistanceUnitType.toString()}
            onChange={this.setDistanceUnitType}
          >
            {Object.keys(DistanceUnitsTypes).map(option => {
              return (
                <option key={option} value={option}>
                  {option}
                </option>
              )
            })}
          </StruxhubSelect>
        )}
        <StruxhubInput
          label={propertiesPanel.radius}
          customRightIcon={
            this.isSelectedSitemapReferenced &&
            this.selectedCraneDistanceUnitType.toString()
          }
          isRequiredTextHidden={true}
          type="number"
          step={DESIMAL_LINE_WIDTH_STEP}
          value={this.circleRadius.toFixed(0)}
          onChange={this.onRadiusChange}
          withDelay={true}
          min={MIN_RADIUS}
          max={MAX_RADIUS}
        />
        <div className="row">
          <div className="no-grow">
            <Checkbox
              checked={circle.isDivided}
              onCheckedChange={() => this.toggleCircleIsDivided()}
            />
          </div>
          <div className="text large pl8">
            {propertiesPanel.limitSwingRadius}
          </div>
        </div>
        {circle.isDivided && (
          <>
            <StruxhubInput
              label={propertiesPanel.divisionStartAngle}
              customRightIcon="°"
              isRequiredTextHidden={true}
              type="number"
              step={LINE_WIDTH_STEP}
              min={MIN_ANGLE}
              max={MAX_ANGLE}
              negativeOrDecimal={true}
              value={this.startAngle}
              onChange={this.onStartAngleChange}
              withDelay={true}
            />
            <StruxhubInput
              label={propertiesPanel.divisionEndAngle}
              customRightIcon="°"
              isRequiredTextHidden={true}
              type="number"
              step={LINE_WIDTH_STEP}
              min={MIN_ANGLE}
              max={MAX_ANGLE}
              negativeOrDecimal={true}
              value={this.endAngle}
              onChange={this.onEndAngleChange}
              withDelay={true}
            />
          </>
        )}
      </div>
    )
  }

  @computed
  private get circleRadius(): number {
    if (this.isGlobeMode) {
      const {
        sitemapItem: { shapeCoordinates },
      } = this.selectedMapViewItem
      if (
        shapeCoordinates.type !== SitemapItemShapeType.Circle ||
        shapeCoordinates.coordinates.length < 2
      ) {
        return 0
      }

      return (shapeCoordinates as CircleShapeCoordinates).getRadius(
        this.selectedCraneDistanceUnitType,
      )
    } else {
      return (this.itemProperties.shapeProperties as SitemapCircleProperties)
        .radius
    }
  }
  @action.bound
  private onEndAngleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    const { shapeProperties } = this.itemProperties
    const circle = shapeProperties as SitemapCircleProperties
    const circleShapeCoords = shapeCoordinates as CircleShapeCoordinates
    const angle = Number(this.verifyAngle(event.target.value))

    if (!isNaN(angle)) {
      if (this.isGlobeMode) {
        circleShapeCoords.divisionEndAngle = angle
      } else {
        circle.setDivisionEndAngle(angle)
      }
    }
  }

  @action.bound
  private onStartAngleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    const { shapeProperties } = this.itemProperties
    const circle = shapeProperties as SitemapCircleProperties
    const circleShapeCoords = shapeCoordinates as CircleShapeCoordinates
    const angle = Number(this.verifyAngle(event.target.value))

    if (!isNaN(angle)) {
      if (this.isGlobeMode) {
        circleShapeCoords.divisionStartAngle = angle
      } else {
        circle.setDivisionStartAngle(angle)
      }
    }
  }

  @action.bound
  private verifyAngle(angleStr: string): string {
    const angle = Number(angleStr)

    if (angle > MAX_ANGLE) {
      return MAX_ANGLE.toString()
    } else if (angle < MIN_ANGLE) {
      return MIN_ANGLE.toString()
    }
    return angleStr
  }

  @action.bound
  private onRadiusChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const radius = +event.target.value

    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    const newPoint = turf.destination(
      [
        shapeCoordinates.coordinates[0].longitude,
        shapeCoordinates.coordinates[0].latitude,
      ],
      radius,
      -90,
      { units: this.selectedCraneDistanceUnitType },
    ).geometry.coordinates
    shapeCoordinates.coordinates[1] = {
      latitude: newPoint[1],
      longitude: newPoint[0],
    }
  }

  @action.bound
  private toggleCircleIsDivided(): void {
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    const { shapeProperties } = this.itemProperties
    if (this.isGlobeMode) {
      const circle = shapeCoordinates as CircleShapeCoordinates
      circle.isClosed = !circle.isClosed
      if (circle.isDivided) {
        circle.divisionStartAngle = 170
        circle.divisionEndAngle = 180
      } else {
        circle.divisionEndAngle = null
        circle.divisionStartAngle = null
      }
    } else {
      const circle = shapeProperties as SitemapCircleProperties
      circle.toggleIsDivided()
      this.addCurrentStateToHistory()
    }
  }

  private renderTextStyle(): JSX.Element {
    const { isDataLess, isRichTextBox } = this.selectedMapViewItem
    const { labelProperties } = this.itemProperties
    if (
      !labelProperties ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Label)
    ) {
      return null
    }

    return (
      <div className="properties-section pa12">
        <div className="text title uppercase">{propertiesPanel.textStyle}</div>
        <div className="tw:pb-3">
          <div className="tw:w-24">
            <StruxhubInput
              label={propertiesPanel.fontSize}
              customRightIcon="px"
              isRequiredTextHidden={true}
              type="number"
              step={FONT_STEP}
              min={MIN_FONT_VALUE}
              max={MAX_FONT_VALUE}
              value={labelProperties.fontSize.toString()}
              onChange={this.onFontSizeChange}
              withDelay={true}
            />
          </div>

          <div className="tw:flex tw:items-center tw:gap-4 tw:pb-2">
            <div className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                checked={labelProperties.isTextBoxDisplayed}
                disabled={!labelProperties.isDisplayed}
                onCheckedChange={() => this.toggleTextBoxVisibility()}
              />
              <span className="tw:text-body-md tw:text-foreground">
                {propertiesPanel.showTextBox}
              </span>
            </div>
            {!isDataLess && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <Checkbox
                  checked={labelProperties.isDisplayed}
                  onCheckedChange={() => this.toggleLabelVisibility()}
                />
                <span className="tw:text-body-md tw:text-foreground">
                  {propertiesPanel.showLabel}
                </span>
              </div>
            )}
          </div>
        </div>
        {isRichTextBox && labelProperties.isTextBoxDisplayed && (
          <div className="tw:pb-3">
            <div className="tw:w-fit">
              <div className="tw:pb-1 tw:text-body-sm tw:text-foreground">
                {propertiesPanel.fontColor}
              </div>
              <SitemapItemsColorPicker
                value={labelProperties.color}
                onChange={this.onTextColorChange}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  @action.bound
  private onTextColorChange(color: string): void {
    this.itemProperties.labelProperties.color = color
    this.addCurrentStateToHistory()
  }

  @action.bound
  private onFontSizeChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { labelProperties } = this.itemProperties
    labelProperties.setFontSize(Number(event.target.value))
    this.addCurrentStateToHistory()
  }

  @action.bound
  private toggleTextBoxVisibility(): void {
    const { labelProperties } = this.itemProperties
    labelProperties.toggleTextBoxVisibility()
    this.addCurrentStateToHistory()
  }

  @action.bound
  private toggleLabelVisibility(): void {
    const { labelProperties } = this.itemProperties
    labelProperties.toggleIsDisplayed()
    if (
      !labelProperties.isDisplayed &&
      this.mapViewItemsSetupStore.selectedMapViewItemDrawnPart ===
        MapViewItemDrawnPart.Label
    ) {
      this.mapViewItemsSetupStore.deselectMapViewItemDrawnPart()
    }
    this.addCurrentStateToHistory()
  }

  private renderLineStyle(): JSX.Element {
    const {
      sitemapItem: { shapeCoordinates },
    } = this.selectedMapViewItem
    const { shapeProperties } = this.itemProperties

    if (
      !shapeProperties ||
      shapeProperties.type !== SitemapItemShapeType.Polyline ||
      this.isPartPropertiesHidden(MapViewItemDrawnPart.Shape)
    ) {
      return null
    }

    const poly = shapeProperties as SitemapPolyLineProperties

    if (
      (this.isGlobeMode && shapeCoordinates?.isClosed) ||
      (!this.isGlobeMode && poly?.isClosed)
    ) {
      return null
    }

    const options = this.isGlobeMode
      ? this.lineArrowGlobePositionOptions
      : this.lineArrowPositionOptions
    const value = options.map(o => o.value).includes(poly.arrowPosition)
      ? poly.arrowPosition
      : SitemapLineArrowPosition.None

    return (
      <div className="properties-section pa12">
        <div className="text title uppercase pb12">
          {propertiesPanel.lineStyle}
        </div>
        <div>
          <div className="text pb4">{value}</div>
          <div>
            <PropertySelect
              options={options}
              value={poly.arrowPosition}
              onChange={this.onLineArrowPositionChange}
            />
          </div>
        </div>
      </div>
    )
  }

  @computed
  private get startAngle(): string {
    if (!this.itemProperties?.shapeProperties) {
      return
    }
    const circle = this.itemProperties
      .shapeProperties as SitemapCircleProperties

    const circleShapeCoords = this.selectedMapViewItem.sitemapItem
      .shapeCoordinates as CircleShapeCoordinates

    return Math.round(
      this.isGlobeMode
        ? circleShapeCoords.divisionStartAngle
        : circle.divisionStartAngle,
    ).toString()
  }

  @computed
  private get endAngle(): string {
    if (!this.itemProperties?.shapeProperties) {
      return
    }
    const circle = this.itemProperties
      .shapeProperties as SitemapCircleProperties

    const circleShapeCoords = this.selectedMapViewItem.sitemapItem
      .shapeCoordinates as CircleShapeCoordinates

    return Math.round(
      this.isGlobeMode
        ? circleShapeCoords.divisionEndAngle
        : circle.divisionEndAngle,
    ).toString()
  }

  @action.bound
  private onLineArrowPositionChange(value: SitemapLineArrowPosition): void {
    const { shapeProperties } = this.itemProperties
    const poly = shapeProperties as SitemapPolyLineProperties
    poly.setArrowPosition(value)
    this.addCurrentStateToHistory()
  }

  private isPartPropertiesHidden(part: MapViewItemDrawnPart): boolean {
    // The embedded resource card shows every part's properties together: it's a
    // form, not the canvas-focused editor, so selecting a drawn part on the map
    // shouldn't collapse the panel down to just that part (icon/shape/line all
    // stay visible).
    if (this.props.embedded) {
      return false
    }
    const selectedPart =
      this.mapViewItemsSetupStore.selectedMapViewItemDrawnPart
    return selectedPart && selectedPart !== part
  }

  @action.bound
  private addCurrentStateToHistory(handleBlur?: () => void): void {
    handleBlur?.()
    this.mapViewItemsSetupStore.addCurrentStateToHistory()
  }

  private renderBuildingsProperties(): JSX.Element {
    const { dataObject } = this.selectedMapViewItem
    if (!dataObject || dataObject.type !== LocationType.Building) {
      return null
    }

    const building = dataObject as Building

    return (
      <BuildingProperties
        building={building}
        store={this.mapViewItemsSetupStore}
        setLoading={this.setLoading}
      />
    )
  }

  // Vertical objects only: equipment keeps no accessible levels on the map.
  private renderObjectAccessibleLevelsProperties(): JSX.Element {
    const { dataObject } = this.selectedMapViewItem
    if (dataObject?.type !== LocationType.VerticalObject) return null

    return (
      <ObjectAccessibleLevelsProperties
        className="relative"
        dataObject={dataObject as VerticalObject}
      />
    )
  }

  @action.bound
  private toggleIsLinked(): void {
    const projectLocation = this.selectedMapViewItem
      .dataObject as ProjectLocation
    projectLocation.isLinked = !projectLocation.isLinked
  }

  private get projectAddressString(): string {
    const { projectAddress } = this.props.state
    const { address, city, state, zipcode, country } = projectAddress

    return `${address} ${city} ${state} ${zipcode} ${country}`
  }

  private get newProjectAddressString(): string {
    const { newProjectAddress } = this.store.mapViewItemsSetupStore
    if (!newProjectAddress) {
      return null
    }
    const { address, city, state, zipcode, country } = newProjectAddress

    return `${address} ${city} ${state} ${zipcode} ${country}`
  }

  @action.bound
  private async mapBoxOnQueryChange(query: string): Promise<void> {
    const data: GeoJSON.FeatureCollection = await getMapBoxPlaces(
      query,
      mapboxgl.accessToken,
    )

    this.projectSettingsStore.setProjectAddressSuggestions(
      data.features ? (data.features as IMapBoxFeature[]) : [],
    )
  }

  @action.bound
  private mapBoxOnAddressValueChanged(item: IMapBoxFeature) {
    if (!item) {
      return
    }
    if (
      this.selectedMapViewItem &&
      this.selectedMapViewItem.sitemapItem &&
      (this.selectedMapViewItem.dataObject as ProjectLocation)?.isLinked
    ) {
      runInAction(() => {
        const { shapeCoordinates, coordinates } =
          this.selectedMapViewItem.sitemapItem

        const addr = mapboxFeatureToAddress(item)
        this.store.mapViewItemsSetupStore.newProjectAddress = addr

        shapeCoordinates?.translate(
          addr.center.lat - coordinates.latitude,
          addr.center.lng - coordinates.longitude,
        )

        this.selectedMapViewItem.sitemapItem.coordinates = {
          latitude: addr.center.lat,
          longitude: addr.center.lng,
        }

        this.store.mapBoxViewerStore.setViewportFromItem(
          this.selectedMapViewItem,
        )
      })
    }
  }

  @action.bound
  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  @computed
  public get shouldShowLoading(): boolean {
    return (
      this.selectedMapViewItem &&
      (this.isLoading || this.mapViewItemsSetupStore.isSitemapUpdating)
    )
  }

  private get sitemapsSetupStore(): SitemapsSetupStore {
    return this.store.sitemapsSetupStore
  }

  private get mapViewItemsSetupStore(): MapViewItemsSetupStore {
    return this.store.mapViewItemsSetupStore
  }

  private get selectedMapViewItem(): MapViewItemBase {
    return this.mapViewItemsSetupStore.selectedMapViewItem
  }

  private get isSelectedSitemapReferenced(): boolean {
    return this.sitemapsSetupStore.selectedSitemap?.isReferenced
  }
}
